import { App as VueApp, createApp } from 'vue'
import { AppRootComponent } from '@/app/root'
import { IAppApplicationSetup } from '@/app/setup'
import { initializeI18n } from '@/i18n/bootstrap'
import type { AppStore } from '@/store/types'
import { getErrorMessage, isHandledError, isResponseException, markAsHandledError, pushSnackbarMessage } from '@/app/errors'

export type AppMountTarget = string | Element
export type AppInstance = VueApp<Element>

let unhandledRejectionListenerInstalled = false

function installRuntimeErrorHandling(app: AppInstance, store?: AppStore): void {
  app.config.errorHandler = (error) => {
    if (isHandledError(error)) return

    if (isResponseException(error)) {
      pushSnackbarMessage(store, error.exceptionMessage)
      markAsHandledError(error)
      return
    }

    console.error(error)
  }

  if (unhandledRejectionListenerInstalled || typeof window === 'undefined') return

  window.addEventListener('error', (event) => {
    if (isHandledError(event.error)) {
      event.preventDefault()
    }
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (isHandledError(event.reason)) {
      event.preventDefault()
      return
    }

    if (isResponseException(event.reason)) {
      pushSnackbarMessage(store, event.reason.exceptionMessage)
      markAsHandledError(event.reason)
      event.preventDefault()
      return
    }

    const message = getErrorMessage(event.reason)
    if (message === 'Not allowed in impersonation mode') {
      pushSnackbarMessage(store, message)
      event.preventDefault()
    }
  })

  unhandledRejectionListenerInstalled = true
}

export function createAppInstance(rootComponent: AppRootComponent, setup: IAppApplicationSetup): AppInstance {
  const app = createApp(rootComponent as any)
  const store = setup.integrations.store as AppStore | undefined

  if (setup.integrations.store) app.use(setup.integrations.store as any)
  if (setup.integrations.router) app.use(setup.integrations.router as any)

  setup.plugins.forEach(({ plugin, options }) => {
    app.use(plugin as any, ...(options as any[]))
  })

  setup.components.forEach(({ name, definition }) => {
    app.component(name, definition as any)
  })

  setup.globalProperties.forEach(({ name, value }) => {
    app.config.globalProperties[name] = value
  })

  installRuntimeErrorHandling(app, store)

  return app
}

export function mountAppInstance(app: AppInstance, target: AppMountTarget = '#app'): AppInstance {
  app.mount(target)
  return app
}

export function mountRootApp(
  rootComponent: AppRootComponent,
  setup: IAppApplicationSetup,
  target: AppMountTarget = '#app',
): AppInstance {
  const app = createAppInstance(rootComponent, setup)
  // i18n must be initialized after plugin installation so the global composer is ready.
  initializeI18n()
  return mountAppInstance(app, target)
}
